import { useMutation } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import { novaClient } from "@/lib/trpc";
import {
  ApplyEnrichmentArgs,
  ApplyEnrichmentNovaInput,
  ApplyEnrichmentResult,
  Candidacy,
  HttpResponse,
  UpdateCandidacyRequest,
} from "@/types";

// Overwrites the candidate's profile with the accepted enrichment.
export function useApplyEnrichment() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const novaClientUtils = novaClient.useUtils();
  const setCandidacyInfo = useSetCandidacyInfo();

  const userApply =
    novaClient.internal.user.active.applyEnrichment.useMutation();

  return useMutation<ApplyEnrichmentResult, Error, ApplyEnrichmentArgs>({
    mutationFn: async ({ candidacyId, enrichedProfile, userId }) => {
      const { executiveSummary, ...rest } = enrichedProfile;
      // Null means the session never produced that section — omit it so nova
      // leaves the saved data untouched. Explicit clears arrive as ""/[].
      const enrichment = Object.fromEntries(
        Object.entries(rest).filter(([, value]) => value !== null),
      ) as ApplyEnrichmentNovaInput;
      const candidacyPath = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/${candidacyId}`;

      const [candidacyResult, profileResult] = await Promise.allSettled([
        executiveSummary !== null
          ? guacHttpClient.patch<
              Candidacy,
              HttpResponse<Candidacy>,
              UpdateCandidacyRequest
            >(candidacyPath, { summary: executiveSummary })
          : Promise.resolve(null),
        userApply.mutateAsync({ userId, enrichment }),
      ]);

      if (
        candidacyResult.status === "rejected" &&
        profileResult.status === "rejected"
      ) {
        throw new Error("Failed to save executive summary and profile.");
      }
      if (candidacyResult.status === "rejected") {
        throw new Error("Failed to save executive summary.");
      }
      if (profileResult.status === "rejected") {
        throw new Error("Failed to save profile.");
      }

      return {
        candidacy: candidacyResult.value ? candidacyResult.value.data : null,
        profile: profileResult.value,
      };
    },
    onSuccess: ({ candidacy }, { candidacyId }) => {
      if (candidacy) {
        setCandidacyInfo({ candidacyId, summary: candidacy.summary });
      }
      // The sheet renders the profile from this query — refresh it with the
      // enriched sections.
      void novaClientUtils.internal.user.active.getCandidacySheetData.invalidate();
    },
  });
}
